import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SvgIcon } from '../../Common/SvgIcon';
import { Link } from 'react-router-dom';
import { MenuItem, useMenu } from '@/hooks/useMenu';
import { useUnit } from 'effector-react';
import { $user } from '@/models/user';

interface SidebarProps {
  isOpen: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});
  const location = useLocation();
  const menu = useMenu();

  const user = useUnit($user);

  const handleItemClick = (item: MenuItem, event: React.MouseEvent) => {
    if (item.submenu) {
      setOpenItems((prev) => ({
        ...prev,
        [item.title]: !prev[item.title],
      }));
      event.preventDefault();
    } else if (item.onClick) {
      item.onClick(event);
    }
  };

  const isItemOpen = (item: MenuItem) =>
    openItems[item.title] === undefined ? Boolean(item.active?.()) : openItems[item.title];
  const isActiveLink = (link?: string) => link && location.pathname === link;

  const renderLink = (item: MenuItem, isSubmenu: boolean = false) => {
    if (item.allowedGroups && !item.allowedGroups.some((group) => user?.groups?.includes(group))) {
      return null;
    }
    if (
      item.hiddenFromGroups &&
      item.hiddenFromGroups.some((group) => user?.groups?.includes(group))
    ) {
      return null;
    }
    const linkProps = {
      to: item.link || '#',
      className: `
        flex items-center ${isSubmenu ? 'justify-start' : 'justify-between'} 
        px-6 py-4 text-white text-sm uppercase tracking-wide border-l-3 
        transition-all duration-200 hover:bg-white/5
        ${item.active?.() && !item.submenu ? 'border-[#6f6eff] bg-white/5' : 'border-transparent'}
        ${isItemOpen(item) ? 'bg-white/5' : ''}
      `,
      onClick: (e: React.MouseEvent) => handleItemClick(item, e),
      ...(item.external && {
        rel: 'noopener noreferrer',
        target: '_blank',
      }),
    };

    return (
      <Link {...linkProps}>
        <div className="flex items-center flex-1">
          {item.ico && (
            <SvgIcon
              name={item.ico}
              className="mr-4 flex-shrink-0"
              size={20}
              color={item.active?.() && !item.submenu ? '#6f6eff' : 'rgba(255,255,255,0.7)'}
              fallback={<div className="w-5 h-5 mr-4 bg-white/50 rounded flex-shrink-0"></div>}
            />
          )}
          <span className="truncate">{item.title}</span>
        </div>
        {!isSubmenu && item.submenu && (
          <svg
            className={`w-4 h-4 transition-transform duration-300 ${
              isItemOpen(item) ? 'rotate-180' : ''
            }`}
            viewBox="0 0 12 12"
            fill="currentColor"
          >
            <path d="M6 8.825L1.175 4 2.25 2.925 6 6.675 9.75 2.925 10.825 4z" />
          </svg>
        )}
      </Link>
    );
  };

  const renderSubmenuLink = (subitem: MenuItem) => {
    const linkProps = {
      to: subitem.link || '#',
      className: `
        flex items-center px-6 py-3 text-white text-sm border-l-3 
        transition-all duration-200 hover:bg-white/5
        ${isActiveLink(subitem.link) ? 'border-[#6f6eff] bg-white/5' : 'border-transparent'}
      `,
      onClick: (e: React.MouseEvent) => {
        if (subitem.onClick) {
          subitem.onClick(e);
        }
      },
      ...(subitem.external && {
        rel: 'noopener noreferrer',
        target: '_blank',
      }),
    };

    return (
      <Link {...linkProps}>
        {subitem.ico && (
          <SvgIcon
            name={subitem.ico}
            className="mr-5 flex-shrink-0"
            size={16}
            color={isActiveLink(subitem.link) ? '#6f6eff' : 'rgba(255,255,255,0.5)'}
            fallback={<div className="w-4 h-4 mr-5 bg-white/30 rounded flex-shrink-0"></div>}
          />
        )}
        <span
          className={`
          truncate
          ${isActiveLink(subitem.link) ? 'opacity-100 text-white' : 'opacity-50'}
        `}
        >
          {subitem.title}
        </span>
      </Link>
    );
  };

  return (
    <nav
      className={`
       fixed bottom-0 left-0 w-72 h-[calc(100%-70px)] bg-[#3b4069] z-40 
        overflow-y-auto transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
    >
      <ul>
        {menu.map((item, index) => (
          <li
            key={index}
            className={`
              border-t border-white/10
              ${isActiveLink(item.link) && !item.submenu ? 'bg-white/5' : ''}
            `}
          >
            {renderLink(item)}

            {item.submenu && isItemOpen(item) && (
              <div className="bg-[#2d3254]">
                <ul>
                  {item.submenu.map((subitem, subIndex) => (
                    <li key={subIndex}>{renderSubmenuLink(subitem)}</li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};
